import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, animate } from 'framer-motion';
import {
  Plus,
  Pencil,
  UtensilsCrossed,
  Tag,
  Wrench,
  Snowflake,
  ShieldCheck,
  PhoneCall,
  Bot,
  UserPlus,
  CalendarCheck,
  Check
} from 'lucide-react';

/*
 * The hero phone shows the BUSINESS OWNER managing their store from the Versa
 * Launch app — adding menu items, products and services — plus an AI receptionist.
 * Owner-facing back-office, not the customer's checkout. Switching is handled in
 * PhoneDemo; the management lists auto-scroll, the receptionist plays a short call.
 */

const EASE = [0.22, 0.61, 0.36, 1];
const APP_ICON = '/favicons/apple-touch-icon.png';

function useAutoScroll(y, scrollRef, portRef, containerRef, disabled) {
  useEffect(() => {
    if (disabled) return undefined;
    let controls = null;
    let cleanup = null;
    const raf = requestAnimationFrame(() => {
      const max = Math.max(0, (scrollRef.current?.scrollHeight || 0) - (portRef.current?.clientHeight || 0));
      if (max <= 8) return;
      controls = animate(y, [0, -max, -max, 0], {
        duration: 13,
        times: [0, 0.46, 0.54, 1],
        ease: 'easeInOut',
        repeat: Infinity,
        autoplay: false
      });
      const inView = { current: true };
      const visible = { current: true };
      const sync = () => {
        if (!controls) return;
        if (inView.current && visible.current) controls.play();
        else controls.pause();
      };
      let io = null;
      if (containerRef?.current && 'IntersectionObserver' in window) {
        io = new IntersectionObserver(
          (entries) => { inView.current = entries[0]?.isIntersecting ?? true; sync(); },
          { threshold: 0.15 }
        );
        io.observe(containerRef.current);
      }
      const onVis = () => { visible.current = !document.hidden; sync(); };
      document.addEventListener('visibilitychange', onVis);
      sync();
      cleanup = () => { if (io) io.disconnect(); document.removeEventListener('visibilitychange', onVis); };
    });
    return () => {
      cancelAnimationFrame(raf);
      if (cleanup) cleanup();
      if (controls) controls.stop();
    };
  }, [y, scrollRef, portRef, containerRef, disabled]);
}

function AddBtn() {
  return (
    <span className="vapp-addbtn" aria-hidden="true"><Plus size={16} strokeWidth={2.8} /></span>
  );
}

function AppShell({ name, sub, right, scroll = true, reduce, containerRef, children }) {
  const y = useMotionValue(0);
  const scrollRef = useRef(null);
  const portRef = useRef(null);
  useAutoScroll(y, scrollRef, portRef, containerRef, reduce || !scroll);

  return (
    <div className="vapp">
      <div className="vapp-bar">
        <span className="vapp-appicon">
          <img src={APP_ICON} alt="" draggable="false" />
        </span>
        <div className="vapp-bar-text">
          <span className="vapp-name">{name}</span>
          <span className="vapp-loc">{sub}</span>
        </div>
        {right}
      </div>
      <div className="vapp-port" ref={portRef}>
        <motion.div className="vapp-scroll" ref={scrollRef} style={scroll ? { y } : undefined}>
          {children}
        </motion.div>
      </div>
    </div>
  );
}

function Tabs({ items }) {
  return (
    <div className="vapp-mtabs">
      {items.map((t, i) => (
        <span key={t} className={i === 0 ? 'is-active' : ''}>{t}</span>
      ))}
    </div>
  );
}

/* One management row: product / menu item / service the owner manages. */
function MRow({ img, icon: Icon, thumb, name, price, status, tone = 'gold', added = false, dim = false, layout = false, delay = 0, reduce }) {
  return (
    <motion.div
      layout={!reduce && layout}
      className={`vapp-mrow ${added ? 'is-new' : ''}`}
      initial={reduce ? false : { opacity: 0, y: added ? -12 : 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.36, delay: reduce ? 0 : delay, ease: EASE }}
    >
      {img ? (
        <span className={`vapp-mthumb ${dim ? 'is-dim' : ''}`} style={{ backgroundImage: `url(${img})` }} />
      ) : Icon ? (
        <span className="vapp-mthumb vapp-mthumb-icon"><Icon size={18} strokeWidth={2.2} /></span>
      ) : (
        <span className={`vapp-mthumb thumb-${thumb}`} />
      )}
      <div className="vapp-mrow-main">
        <span className="vapp-mrow-name">{name}</span>
        <span className="vapp-mrow-meta">
          <span className="vapp-mrow-price">{price}</span>
          <span className={`vapp-status tone-${tone}`}><span className="vapp-status-dot" />{status}</span>
        </span>
      </div>
      {added ? (
        <span className="vapp-newtag"><Check size={11} strokeWidth={3} />Added</span>
      ) : (
        <span className="vapp-edit" aria-hidden="true"><Pencil size={13} strokeWidth={2.3} /></span>
      )}
    </motion.div>
  );
}

function AddRow({ label, reduce, delay, pulse = false }) {
  return (
    <motion.div
      className="vapp-addrow"
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={
        pulse && !reduce
          ? { opacity: 1, y: 0, scale: [1, 0.96, 1.03, 1] }
          : { opacity: 1, y: 0 }
      }
      transition={{ duration: 0.36, delay: reduce ? 0 : delay, ease: EASE, scale: { duration: 0.5, delay: 0, ease: EASE } }}
    >
      <Plus size={15} strokeWidth={2.8} />{label}
    </motion.div>
  );
}

/* Reveals (false -> true) once after `delay`; resets each time the app remounts
   in the cycle, so every appearance replays the "owner just added an item" beat. */
function useReveal(delay, reduce) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (reduce) { setShown(true); return undefined; }
    const t = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(t);
  }, [delay, reduce]);
  return shown;
}

/* The "· N" count that bumps when a new item is added. */
function Counter({ value, reduce }) {
  return (
    <motion.span
      key={value}
      initial={reduce ? false : { scale: 0.65, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.3, ease: EASE }}
      style={{ display: 'inline-block' }}
    >
      · {value}
    </motion.span>
  );
}

/* Management list that plays a clean "item added" sequence on every appearance:
   the new row drops into the top with an Added tag, the rest slide down, the
   count ticks up, and the Add row gives a quick press. */
function ManagedList({ sectionLabel, baseCount, newItem, items, addLabel, reduce }) {
  const shown = useReveal(1500, reduce);
  const count = baseCount + (shown ? 1 : 0);
  return (
    <>
      <div className="vapp-msec">{sectionLabel} <Counter value={count} reduce={reduce} /></div>
      <div className="vapp-mlist">
        <AnimatePresence initial={false}>
          {shown && <MRow key="added-item" {...newItem} added reduce={reduce} />}
        </AnimatePresence>
        {items.map((it, i) => (
          <MRow key={it.name} {...it} layout delay={reduce ? 0 : 0.12 + i * 0.12} reduce={reduce} />
        ))}
      </div>
      <AddRow label={addLabel} reduce={reduce} pulse={shown} delay={reduce ? 0 : 0.12 + items.length * 0.12} />
    </>
  );
}

/* ----------------------------------------------------------- FOOD (menu mgr) */
function AppFood({ reduce, containerRef }) {
  return (
    <AppShell
      name="Coastal Kitchen"
      sub={<><UtensilsCrossed size={10} strokeWidth={2.4} />Menu manager</>}
      right={<AddBtn />}
      reduce={reduce}
      containerRef={containerRef}
    >
      <Tabs items={['Live', 'Draft', 'All']} />
      <ManagedList
        sectionLabel="Menu items"
        baseCount={24}
        newItem={{ img: '/hero-app/spicy-tuna-bowl.jpg', name: 'Spicy Tuna Bowl', price: '$16.50', status: 'Live', tone: 'gold' }}
        items={[
          { img: '/hero-app/dragon-roll.jpg', name: 'Dragon Roll', price: '$14.00', status: 'Live', tone: 'gold' },
          { img: '/hero-app/tonkotsu-ramen.jpg', name: 'Tonkotsu Ramen', price: '$13.50', status: 'Draft', tone: 'muted', dim: true }
        ]}
        addLabel="Add menu item"
        reduce={reduce}
      />
    </AppShell>
  );
}

/* ----------------------------------------------------- SHOP (product mgr) */
function AppShop({ reduce, containerRef }) {
  return (
    <AppShell
      name="Harbor Goods"
      sub={<><Tag size={10} strokeWidth={2.4} />Product manager</>}
      right={<AddBtn />}
      reduce={reduce}
      containerRef={containerRef}
    >
      <Tabs items={['Active', 'Draft', 'All']} />
      <ManagedList
        sectionLabel="Products"
        baseCount={38}
        newItem={{ img: '/hero-app/linen-shirt.jpg', name: 'Linen Shirt', price: '$48', status: 'Active', tone: 'gold' }}
        items={[
          { img: '/hero-app/canvas-tote.jpg', name: 'Canvas Tote', price: '$32', status: 'Active', tone: 'gold' },
          { img: '/hero-app/sun-hat.jpg', name: 'Sun Hat', price: '$24', status: 'Low stock', tone: 'amber' },
          { img: '/hero-app/deck-shorts.jpg', name: 'Deck Shorts', price: '$39', status: 'Out of stock', tone: 'muted' }
        ]}
        addLabel="Add product"
        reduce={reduce}
      />
    </AppShell>
  );
}

/* ------------------------------------------------- SERVICE (service mgr) */
function AppService({ reduce, containerRef }) {
  return (
    <AppShell
      name="Coastal Air & Plumbing"
      sub={<><Wrench size={10} strokeWidth={2.4} />Service manager</>}
      right={<AddBtn />}
      reduce={reduce}
      containerRef={containerRef}
    >
      <Tabs items={['Active', 'Draft', 'All']} />
      <ManagedList
        sectionLabel="Services"
        baseCount={12}
        newItem={{ icon: Wrench, name: 'AC Repair', price: 'from $89', status: 'Active', tone: 'gold' }}
        items={[
          { icon: Snowflake, name: 'Seasonal Tune-up', price: '$129', status: 'Active', tone: 'gold' },
          { icon: ShieldCheck, name: 'New System Install', price: 'Quote', status: 'Active', tone: 'gold' }
        ]}
        addLabel="Add service"
        reduce={reduce}
      />
    </AppShell>
  );
}

/* ------------------------------------------------------------ AI RECEPTIONIST */
function Bubble({ side, children, delay, reduce }) {
  return (
    <motion.div
      className={`vapp-bubble is-${side}`}
      initial={reduce ? false : { opacity: 0, y: 8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.34, delay: reduce ? 0 : delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

function ResultRow({ icon: Icon, label, status, delay, reduce }) {
  return (
    <motion.div
      className="vapp-resrow"
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.34, delay: reduce ? 0 : delay, ease: EASE }}
    >
      <span className="vapp-resrow-ic"><Icon size={14} strokeWidth={2.4} /></span>
      <div className="vapp-item-main">
        <span className="vapp-item-name">{label}</span>
        <span className="vapp-item-desc">{status}</span>
      </div>
      <Check size={15} strokeWidth={2.8} className="vapp-resrow-check" />
    </motion.div>
  );
}

function AppReceptionist({ reduce, containerRef }) {
  return (
    <AppShell
      name="AI Receptionist"
      sub={<><Bot size={10} strokeWidth={2.4} />Answering for Coastal Air</>}
      right={<span className="vapp-cart vapp-cart-on"><PhoneCall size={16} strokeWidth={2.2} /></span>}
      scroll={false}
      reduce={reduce}
      containerRef={containerRef}
    >
      <div className="vapp-call">
        <span className="vapp-call-ic"><PhoneCall size={15} strokeWidth={2.3} /></span>
        <div className="vapp-item-main">
          <span className="vapp-item-name">Incoming call · (904) 555-0182</span>
          <span className="vapp-item-desc">AI answered in 1 ring</span>
        </div>
      </div>
      <div className="vapp-chat">
        <Bubble side="ai" delay={0.2} reduce={reduce}>Thanks for calling Coastal Air! How can I help?</Bubble>
        <Bubble side="cust" delay={0.9} reduce={reduce}>My AC stopped working this morning.</Bubble>
        <Bubble side="ai" delay={1.7} reduce={reduce}>I can send a tech tomorrow at 9 AM. Want me to book it?</Bubble>
        <Bubble side="cust" delay={2.5} reduce={reduce}>Yes, please.</Bubble>
      </div>
      <div className="vapp-results">
        <ResultRow icon={UserPlus} label="New lead captured" status="Jordan M. · (904) 555-0182" delay={3.2} reduce={reduce} />
        <ResultRow icon={CalendarCheck} label="Appointment booked" status="Tomorrow · 9:00 AM" delay={3.6} reduce={reduce} />
      </div>
    </AppShell>
  );
}

export const APPS = [
  { id: 'food', dwell: 6500, Comp: AppFood },
  { id: 'shop', dwell: 6500, Comp: AppShop },
  { id: 'service', dwell: 6500, Comp: AppService },
  { id: 'receptionist', dwell: 7500, Comp: AppReceptionist }
];

export const SCENE_ALT_TEXT =
  'A preview of the Versa Launch app a business owner uses to run their store: add and manage menu items, ' +
  'products and services in seconds, set them live or to draft, and an AI receptionist that answers calls, ' +
  'captures leads and books appointments automatically.';
